"use client";

import type {
  CCrmProjectManagementContent,
  CCrmProjectManagementPackage,
  CCrmProjectManagementType,
} from "@/lib/api/cms-catalog";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge/Badge";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { OrderForm } from "./OrderForm";
import { buildOrderHash, navigateToOrder, parseOrderRoute } from "./orderRoute";
import {
  cCrmProjectManagementTranslations,
  type CCrmProjectManagementTranslations,
} from "./CCrmProjectManagement.i18n";
import styles from "./CCrmProjectManagement.module.css";

/**
 * CCrmProjectManagement - publiczna treść katalogu `c_crm_project_management`:
 * strona zamawiania usługi z ofertą pakietów w podziale na typy usług.
 *
 * **Dostępna bez logowania** - oferta ma być widoczna dla kogoś, kto konta jeszcze
 * nie ma. Ten sam katalog bywa równolegle rozszerzeniem panelu konta (zakładki
 * Projekty i Usługi w `c_user_accounts`); to niezależny kontrakt komponentu,
 * który tej treści nie dotyczy.
 *
 * **Data source:** `C_CRM_PROJECT_MANAGEMENT_CONTROLLER::getApiContent()` -
 * pakiety wystawione przez administratora do zamówienia z serwisu, pogrupowane
 * po typie usługi, w kolejności ustawionej w module Usługi.
 *
 * **Dwa kroki na jednej stronie:** oferta i formularz zamówienia wybranego pakietu.
 * Formularz zastępuje listę zamiast otwierać się pod nią - klient, który wybrał już
 * pakiet, nie ma po co przewijać obok pozostałych, a "Wróć do oferty" zawsze
 * prowadzi z powrotem.
 *
 * **Krok jest adresowalny** - formularz konkretnego pakietu ma własny URL
 * (`#order-{id}`, zob. {@link ./orderRoute}), więc da się go skopiować i wysłać
 * klientowi albo podlinkować z cennika, a Wstecz/Dalej działają jak wszędzie.
 * Przycisk "Zamawiam" jest linkiem, nie przyciskiem, żeby dało się go otworzyć
 * w nowej karcie i skopiować adres z menu kontekstowego.
 *
 * **Security:** `content.intro` to HTML z edytora panelu przepuszczony przez
 * sanitizer komponentu po stronie PHP - dlatego `dangerouslySetInnerHTML`.
 * Nazwy typów i pakietów to plain text (React je escapuje).
 */
export function CCrmProjectManagement({ content }: CCrmProjectManagementProps) {
  const t = useT(cCrmProjectManagementTranslations);

  /* ID pakietu w zamówieniu; null = widok oferty. Źródłem jest hash adresu, a stan
     tylko go odwzorowuje - dzięki temu wejście z linku, Wstecz i Dalej prowadzą
     dokładnie tam, gdzie wskazuje URL. Server Components hasha nie widzą, więc
     pierwszy render to zawsze oferta, a formularz wchodzi po hydracji. */
  const [orderPackage, setOrderPackage] = useState<number | null>(null);

  useEffect(() => {
    const sync = () => setOrderPackage(parseOrderRoute(window.location.hash));

    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  /* Typ bez pakietów nie ma czego pokazać - sekcja z samym nagłówkiem wyglądałaby
     jak błąd. Backend takich typów nie zwraca (INNER JOIN po pakietach), ale filtr
     trzyma render niezależnym od tego założenia. */
  const types: CCrmProjectManagementType[] = Array.isArray(content.types)
    ? content.types.filter((type) => type.packages.length > 0)
    : [];

  const currency = content.currency ?? "";
  const intro = content.intro?.trim() ?? "";

  if (orderPackage !== null) {
    return (
      <div className={styles.root}>
        <OrderForm
          idPackage={orderPackage}
          currency={currency}
          t={t}
          onBack={() => navigateToOrder(null)}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {intro && (
        <div className={styles.intro} dangerouslySetInnerHTML={{ __html: intro }} />
      )}

      {types.length === 0 ? (
        <div className={styles.empty}>{t.emptyOffer}</div>
      ) : (
        types.map((type) => (
          <section key={type.id} className={styles.type}>
            <header className={styles.typeHeader}>
              <h2 className={styles.typeName}>{type.name}</h2>
              {type.description && (
                <p className={styles.typeDescription}>{type.description}</p>
              )}
            </header>

            <ul className={styles.packages}>
              {type.packages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} currency={currency} t={t} />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

export type CCrmProjectManagementProps = {
  content: CCrmProjectManagementContent;
};

/**
 * Kafelek pojedynczego pakietu.
 *
 * Kolejność bloków odpowiada temu, w jakiej kolejności czyta się ofertę: odznaka
 * wyróżnienia, nazwa, dla kogo, cena z dopiskami, checklista.
 *
 * Główna cena to ta, od której administrator liczy pakiet (`price_from`) - druga
 * idzie pod spodem jako pomocnicza. Bez tego rozróżnienia kafelek dwóch pakietów
 * liczonych z różnych stron pokazywałby kwoty, których nie da się porównać.
 *
 * Dwa dopiski pod ceną są OSOBNYMI polami: `price_note` niesie treść neutralną,
 * `price_highlight` tę, która ma przyciągnąć wzrok. Puste pole nie rezerwuje
 * miejsca, więc kafelki bez dopisków nie rozjeżdżają siatki.
 */
function PackageCard({
  pkg,
  currency,
  t,
}: {
  pkg: CCrmProjectManagementPackage;
  currency: string;
  t: CCrmProjectManagementTranslations;
}) {
  const fromBrutto = pkg.price_from === "brutto";
  const mainPrice = fromBrutto ? pkg.price_brutto : pkg.price_netto;
  const mainLabel = fromBrutto ? t.brutto : t.netto;
  const secondPrice = fromBrutto ? pkg.price_netto : pkg.price_brutto;
  const secondLabel = fromBrutto ? t.netto : t.brutto;

  /* Etykietę odznaki ustawia operator przy pakiecie; bez niej zostaje domyślny
     tekst z tłumaczeń komponentu. */
  const recommendedLabel = pkg.recommended_label?.trim() || t.recommended;

  return (
    <li className={`${styles.package} ${pkg.recommended ? styles.packageRecommended : ""}`}>
      {(pkg.recommended || pkg.priority) && (
        <div className={styles.packageBadges}>
          {pkg.recommended && <Badge variant="primary">{recommendedLabel}</Badge>}
          {pkg.priority && <Badge variant="warning">{t.priority}</Badge>}
        </div>
      )}

      <div className={styles.packageHeader}>
        <h3 className={styles.packageName}>{pkg.name}</h3>
        {pkg.description && (
          <p className={styles.packageDescription}>{pkg.description}</p>
        )}
      </div>

      <div className={styles.price}>
        <span className={styles.priceValue}>
          {mainPrice} {currency}
        </span>
        <span className={styles.priceMeta}>
          {mainLabel}
          {" "}
          {pkg.renew_allowed && pkg.period_label
            ? t.formatPeriod(pkg.period_label)
            : t.oneTime}
        </span>
        <span className={styles.priceSecondary}>
          {secondPrice} {currency} {secondLabel}
        </span>
        {pkg.price_note && <span className={styles.priceNote}>{pkg.price_note}</span>}
        {pkg.price_highlight && (
          <span className={styles.priceHighlight}>{pkg.price_highlight}</span>
        )}
        {/* Bezpłatny test - informacja przy cenie, bo to alternatywa dla zapłaty:
            usługa startuje od razu, a klient płaci dopiero po okresie próbnym. */}
        {pkg.trial_enabled && pkg.trial_days !== null && (
          <span className={styles.priceTrial}>{t.formatTrial(pkg.trial_days)}</span>
        )}
      </div>

      {pkg.features.length > 0 && (
        <ul className={styles.features}>
          {pkg.features.map((feature) => (
            <li key={feature.id} className={styles.feature}>
              <Icon name="check" className={styles.featureIcon} aria-hidden />
              <span>{feature.name}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA zamyka kartę i jest dociśnięte do jej dołu (`margin-top: auto`), żeby
          przyciski wszystkich pakietów stały w jednej linii mimo różnej długości
          checklisty. Link, nie przycisk: adres formularza tego pakietu ma dać się
          otworzyć w nowej karcie i skopiować z menu przeglądarki. */}
      <div className={styles.packageAction}>
        <a className={styles.packageCta} href={buildOrderHash("", pkg.id)}>
          {t.orderCta}
        </a>
      </div>
    </li>
  );
}
